import React, { FC } from 'react';
import { LockIcon } from '@clearcover/atlas';
import { GetGrandPrizeStatusResponse } from 'Rewards/services/endpoints/GetGrandPrizeStatus/types';
import { GetTierStatusResponse } from 'Rewards/services/endpoints/GetTierStatus/types';
import { GetRewardsConfigResponse } from 'Rewards/services/endpoints/GetRewardsConfig/types';
import styles from './styles.module.css';

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${String(d.getUTCFullYear()).slice(-2)}`;
}

function formatDateRange(start: string, end: string): string {
  return `Results for ${formatDate(start)} – ${formatDate(end)}`;
}

/** Render a retention decimal (0.0–1.0) as a percentage — null/undefined shows "—%"; real 0 shows "0.0%" */
function formatRetention(value: number | null | undefined): string {
  if (value == null) return '—%';
  return `${(value * 100).toFixed(1)}%`;
}

export type GrandPrizeCardFullProps = {
  grandPrizeStatus: GetGrandPrizeStatusResponse | null;
  tierStatus: GetTierStatusResponse | null;
  config: GetRewardsConfigResponse | null;
  loading: boolean;
};

export const GrandPrizeCardFull: FC<GrandPrizeCardFullProps> = ({
  grandPrizeStatus,
  tierStatus,
  // config is accepted for prop-API parity with the slide-out GrandPrizeCard but is not
  // used — program dates come from grandPrizeStatus.programStart/programEnd (AC1).
  config: _config,
  loading,
}) => {
  if (loading) {
    return (
      <div className={styles.card} data-testid="grand-prize-card-full">
        <p className={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  const dateRangeText =
    grandPrizeStatus?.programStart && grandPrizeStatus?.programEnd
      ? formatDateRange(
          grandPrizeStatus.programStart,
          grandPrizeStatus.programEnd
        )
      : null;

  const status = grandPrizeStatus;
  const tier = tierStatus?.tier;

  return (
    <div className={styles.card} data-testid="grand-prize-card-full">
      {/* Header — AC1 */}
      <div className={styles.cardHeader}>
        <p className={styles.sectionLabel}>GRAND PRIZE</p>
        {dateRangeText && (
          <span
            className={styles.dateRange}
            data-testid="grand-prize-date-range"
          >
            {dateRangeText}
          </span>
        )}
      </div>

      {/* Prize headline — AC1 */}
      <p className={styles.prizeHeadline} data-testid="grand-prize-headline">
        Worth $10,000
      </p>

      {/* Agency ranking row — AC2/AC3 */}
      {status && (
        <div
          className={styles.rankingRow}
          data-testid="grand-prize-agency-rank-row"
        >
          <span className={styles.rankingLabel}>
            Your agency&apos;s ranking
          </span>
          <span className={styles.rankingValue}>
            {status.agencyRank !== 1 && (
              <LockIcon
                className={styles.lockIcon}
                aria-label="Agency rank locked"
                data-testid="agency-rank-lock"
              />
            )}
            <span data-testid="agency-rank-value">
              #{status.agencyRank} of {status.totalEnrolledAgencies} agencies
            </span>
          </span>
        </div>
      )}

      {/* Producer ranking row — AC4/AC5 */}
      {status && (
        <div
          className={styles.rankingRow}
          data-testid="grand-prize-producer-rank-row"
        >
          <span className={styles.rankingLabel}>Your current ranking</span>
          <span className={styles.rankingValue}>
            {status.producerRank !== null && status.producerRank !== 1 && (
              <LockIcon
                className={styles.lockIcon}
                aria-label="Producer rank locked"
                data-testid="producer-rank-lock"
              />
            )}
            <span data-testid="producer-rank-value">
              {status.producerRank != null ? `#${status.producerRank}` : '—'}
            </span>
          </span>
        </div>
      )}

      {/* Three gates — AC6/AC7 */}
      {status && (
        <div className={styles.gatesRow} data-testid="grand-prize-gates">
          {/* Gate 1: REWARD TIER — itdPace gate, display current tier name */}
          <div
            className={`${styles.gate}${status.gates.itdPace.passing ? ` ${styles.gateUnlocked}` : ''}`}
            data-testid="gate-reward-tier"
          >
            <span className={styles.gateLabel}>Reward Tier</span>
            <span className={styles.gateValue}>
              {tier
                ? tier
                    .split('_')
                    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
                    .join(' ')
                : '—'}
            </span>
            {!status.gates.itdPace.passing && (
              <LockIcon
                className={styles.gateLock}
                aria-label="Gate locked"
                data-testid="gate-reward-tier-lock"
              />
            )}
          </div>

          {/* Gate 2: POLICIES SOLD */}
          <div
            className={`${styles.gate}${status.gates.policyCount.passing ? ` ${styles.gateUnlocked}` : ''}`}
            data-testid="gate-policies-sold"
          >
            <span className={styles.gateLabel}>Policies Sold</span>
            <span
              className={styles.gateValue}
              data-testid="gate-policies-sold-value"
            >
              {status.gates.policyCount.value ?? 0} /{' '}
              {status.gates.policyCount.threshold}
            </span>
            {!status.gates.policyCount.passing && (
              <LockIcon
                className={styles.gateLock}
                aria-label="Gate locked"
                data-testid="gate-policies-sold-lock"
              />
            )}
          </div>

          {/* Gate 3: RETENTION */}
          <div
            className={`${styles.gate}${status.gates.retention.passing ? ` ${styles.gateUnlocked}` : ''}`}
            data-testid="gate-retention"
          >
            <span className={styles.gateLabel}>Retention</span>
            <span
              className={styles.gateValue}
              data-testid="gate-retention-value"
            >
              {formatRetention(status.gates.retention.value)} /{' '}
              {(status.gates.retention.threshold * 100).toFixed(1)}%
            </span>
            {!status.gates.retention.passing && (
              <LockIcon
                className={styles.gateLock}
                aria-label="Gate locked"
                data-testid="gate-retention-lock"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
